import { api } from '../client';
import { createResourceApi } from './factory';
import type { QueryOf, ResponseOf } from '../spec';
import type {
  DispenseReportResponse,
  AcceptInvitationRequest,
  AuditLogResponse,
  DeliveryAssignRequest,
  DeliveryCancelRequest,
  DeliveryRequest,
  DeliveryResponse,
  DriverRequest,
  DriverResponse,
  DriverUpdateRequest,
  GpsDeviceRequest,
  GpsDeviceResponse,
  LinkUnitRequest,
  VehicleRequest,
  VehicleResponse,
  ChangePasswordRequest,
  CreateUserRequest,
  DispenseRequest,
  DispenseResponse,
  DispenseViewResponse,
  DoctorRequest,
  DoctorResponse,
  ForgotPasswordRequest,
  GovernorateResponse,
  ImportRunResponse,
  InviteVerifyRequest,
  InvoiceExtractionRequest,
  InvoiceExtractionResponse,
  LicenseRequest,
  LicenseResponse,
  LicenseReviewRequest,
  LoginRequest,
  MedicationSuggestionQuery,
  MedicationSuggestionResponse,
  MedicineSearchQuery,
  MessageDTO,
  NotificationResponse,
  OrganizationRequest,
  OrganizationResponse,
  OrganizationStatusRequest,
  OrganizationTypeRequest,
  OrganizationTypeResponse,
  Paginated,
  PatientRequest,
  PatientResponse,
  PrescriptionRequest,
  PrescriptionResponse,
  RegisterRequest,
  ResetPasswordRequest,
  RefillRequest,
  RoleRequest,
  RoleResponse,
  SendOtpRequest,
  SpecialtyRequest,
  SpecialtyResponse,
  SyndicateRegistrySearchQuery,
  DownloadUrlResponse,
  OnboardingStatusResponse,
  StatsResponse,
  TokenResponse,
  UpdateUserRequest,
  UpdateUserStatusRequest,
  UploadResponse,
  UserResponse,
  UsernameAvailability,
  VerifyOtpRequest,
  VisitRequest,
  VisitResponse,
  VisitStatusRequest,
} from '@/types/api';

export const authApi = {
  login: (body: LoginRequest) => api.post<TokenResponse>('/api/v1/auth/login', body),
  register: (body: RegisterRequest) => api.post<MessageDTO>('/api/v1/auth/register', body),
  sendOtp: (body: SendOtpRequest) => api.post<MessageDTO>('/api/v1/auth/otp/send', body),
  verifyOtp: (body: VerifyOtpRequest) => api.post<TokenResponse>('/api/v1/auth/otp/verify', body),
  refresh: (refreshToken: string) =>
    api.post<TokenResponse>('/api/v1/auth/refresh', { refreshToken }),
  logout: (refreshToken: string) => api.post<MessageDTO>('/api/v1/auth/logout', { refreshToken }),
  forgotPassword: (body: ForgotPasswordRequest) =>
    api.post<MessageDTO>('/api/v1/auth/password/forgot', body),
  resetPassword: (body: ResetPasswordRequest) =>
    api.post<MessageDTO>('/api/v1/auth/password/reset', body),
  changePassword: (body: ChangePasswordRequest) =>
    api.post<MessageDTO>('/api/v1/auth/password/change', body),
  /**
   * Accepting an invitation, in two public steps. `verify` checks the code
   * without consuming it so the "choose a password" step can be shown;
   * `accept` consumes it and answers the same TokenResponse as login, so the
   * invitee lands in the app instead of on a sign-in form.
   *
   * An unknown username answers exactly what a wrong code does (OTP_INVALID)
   * on purpose — neither endpoint may reveal which usernames exist.
   */
  verifyInvite: (body: InviteVerifyRequest) =>
    api.post<MessageDTO>('/api/v1/auth/invite/verify', body),
  acceptInvite: (body: AcceptInvitationRequest) =>
    api.post<TokenResponse>('/api/v1/auth/invite/accept', body),
  /**
   * Sign-up only: tells the user a username is taken before they submit. The
   * endpoint deliberately reveals that an account exists — registration does
   * the same by answering USERNAME_TAKEN — and shares the /auth throttle.
   */
  usernameAvailable: (username: string) =>
    api.get<UsernameAvailability>('/api/v1/auth/username-available', { params: { username } }),
};

/**
 * Object storage. Persist `objectKey`, never `url`: the bucket is private and a
 * link lasts ten minutes, so one is fetched at render time.
 *
 * `download-url` is tenant-scoped — another organization's key answers 403 —
 * and an unknown key no longer fails here: the link is issued and 404s when it
 * is followed.
 */
export const filesApi = {
  upload: (file: File, folder?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (folder) form.append('folder', folder);
    // The request interceptor drops the instance's JSON Content-Type for
    // FormData, so the browser sets multipart with its own boundary.
    return api.post<UploadResponse>('/api/v1/files', form);
  },
  downloadUrl: (objectKey: string) =>
    api.get<DownloadUrlResponse>('/api/v1/files/download-url', { params: { objectKey } }),
};

/**
 * Where the caller stands in registration.
 *
 * This is the only reliable source of what the app may do. A founder holds full
 * ORG_OWNER permissions from the moment they register — before anyone has
 * verified the organization — so the token's permissions describe intent, not
 * access, and everything outside registration answers
 * ORGANIZATION_NOT_VERIFIED until an admin approves.
 */
export const onboardingApi = {
  get: () => api.get<OnboardingStatusResponse>('/api/v1/onboarding'),
};

/**
 * One request for the dashboard's figures. A field the caller may not see is
 * absent rather than zero — zero would be a claim about data they cannot read.
 * `prescriptionsByStatus` is the exception and always lists every status.
 */
export const statsApi = {
  get: () => api.get<StatsResponse>('/api/v1/stats'),
};

export const usersApi = {
  ...createResourceApi<UserResponse, CreateUserRequest>('/api/v1/users', { updateMethod: 'patch' }),
  me: () => api.get<UserResponse>('/api/v1/users/me'),
  updateMe: (body: UpdateUserRequest) => api.patch<UserResponse>('/api/v1/users/me', body),
  updateOne: (id: string, body: UpdateUserRequest) =>
    api.patch<UserResponse>(`/api/v1/users/${id}`, body),
  updateStatus: (id: string, body: UpdateUserStatusRequest) =>
    api.patch<UserResponse>(`/api/v1/users/${id}/status`, body),
  /**
   * Sends the invitation again and supersedes the outstanding code
   * (USER_WRITE). 409 USER_INVITE_NOT_PENDING once the account has accepted,
   * 429 OTP_RESEND_TOO_SOON with Retry-After inside the cooldown.
   */
  invite: (id: string) => api.post<UserResponse>(`/api/v1/users/${id}/invite`),
  /**
   * Per-status figures for exactly the rows `GET /users` would return under
   * the same filters (`userCounts`, C67, backend `433ccd6`): every status a
   * key, zeros included. Send the list's query string minus `status`.
   */
  counts: (params?: QueryOf<'/api/v1/users/counts', 'get'> | Record<string, unknown>) =>
    api.get<ResponseOf<'/api/v1/users/counts', 'get'>>('/api/v1/users/counts', { params }),
};

export const rolesApi = {
  ...createResourceApi<RoleResponse, RoleRequest>('/api/v1/roles'),
};

export const permissionsApi = {
  list: () => api.get<ResponseOf<'/api/v1/permissions', 'get'>>('/api/v1/permissions'),
};

export const organizationsApi = {
  ...createResourceApi<OrganizationResponse, OrganizationRequest>('/api/v1/organizations'),
  /** The caller's own tenant. 404 for a founder without one, and for platform staff. */
  me: () => api.get<OrganizationResponse>('/api/v1/organizations/me'),
  /**
   * Recipient directory for delivery requests: ACTIVE organizations any
   * verified tenant may address, with `hasPhone` / `hasLocation` instead of
   * the values. The ordinary list answers a tenant only itself.
   */
  directory: (params?: QueryOf<'/api/v1/organizations/directory', 'get'>) =>
    api.get<ResponseOf<'/api/v1/organizations/directory', 'get'>>(
      '/api/v1/organizations/directory',
      {
        params,
      },
    ),
  submit: (id: string) => api.patch<OrganizationResponse>(`/api/v1/organizations/${id}/submit`),
  changeStatus: (id: string, body: OrganizationStatusRequest) =>
    api.patch<OrganizationResponse>(`/api/v1/organizations/${id}/status`, body),
  /**
   * Per-status figures for exactly the rows `GET /organizations` would return
   * under the same filters (`organizationCounts`, C71, backend `433ccd6`).
   * It honours `status` if sent — leave it out for tab badges. A tenant gets
   * 1 or 0.
   */
  counts: (params?: QueryOf<'/api/v1/organizations/counts', 'get'> | Record<string, unknown>) =>
    api.get<ResponseOf<'/api/v1/organizations/counts', 'get'>>('/api/v1/organizations/counts', {
      params,
    }),
};

/**
 * Reference lists answer a bare array — there is no page envelope on these two
 * endpoints. They are wrapped into the same shape as every other list so the
 * table and the resource hooks stay uniform.
 */
function asPage<T>(rows: T[]): Paginated<T> {
  return { pageNumber: 0, pageSize: rows.length, total: rows.length, content: rows };
}

export const organizationTypesApi = {
  ...createResourceApi<OrganizationTypeResponse, OrganizationTypeRequest>(
    '/api/v1/organization-types',
  ),
  list: () =>
    api
      .get<ResponseOf<'/api/v1/organization-types', 'get'>>('/api/v1/organization-types')
      .then(asPage),
};

export const licensesApi = {
  ...createResourceApi<LicenseResponse, LicenseRequest>('/api/v1/licenses'),
  review: (id: string, body: LicenseReviewRequest) =>
    api.patch<LicenseResponse>(`/api/v1/licenses/${id}/review`, body),
};

export const specialtiesApi = {
  ...createResourceApi<SpecialtyResponse, SpecialtyRequest>('/api/v1/specialties'),
  list: () => api.get<ResponseOf<'/api/v1/specialties', 'get'>>('/api/v1/specialties').then(asPage),
};
export const doctorsApi = createResourceApi<DoctorResponse, DoctorRequest>('/api/v1/doctors');
export const patientsApi = createResourceApi<PatientResponse, PatientRequest>('/api/v1/patients');

export const visitsApi = {
  ...createResourceApi<VisitResponse, VisitRequest>('/api/v1/visits'),
  changeStatus: (id: string, body: VisitStatusRequest) =>
    api.patch<VisitResponse>(`/api/v1/visits/${id}/status`, body),
};

export const prescriptionsApi = {
  path: '/api/v1/prescriptions',
  list: (params?: Record<string, unknown>) =>
    api.get<Paginated<PrescriptionResponse>>('/api/v1/prescriptions', { params }),
  get: (id: string) => api.get<PrescriptionResponse>(`/api/v1/prescriptions/${id}`),
  issue: (body: PrescriptionRequest) =>
    api.post<PrescriptionResponse>('/api/v1/prescriptions', body),
  /**
   * Typeahead for the three free-text columns, learned from what prescribers
   * in this organization have already typed (2026-09-26). A plain array, not a
   * page. Platform staff have no organization and get `[]`.
   */
  medicationSuggestions: (params: MedicationSuggestionQuery) =>
    api.get<MedicationSuggestionResponse[]>('/api/v1/prescriptions/medication-suggestions', {
      params,
    }),
  markPrinted: (id: string) =>
    api.patch<PrescriptionResponse>(`/api/v1/prescriptions/${id}/printed`),
  cancel: (id: string) => api.patch<PrescriptionResponse>(`/api/v1/prescriptions/${id}/cancel`),
};

export const pharmacyApi = {
  /**
   * Succeeds on a fully dispensed prescription when an item is still
   * refillable, so the counter can discover a repeat.
   */
  scan: (qrToken: string) =>
    api.post<DispenseViewResponse>('/api/v1/pharmacy/prescriptions/scan', { qrToken }),
  dispense: (body: DispenseRequest) =>
    api.post<DispenseResponse>('/api/v1/pharmacy/prescriptions/dispense', body),
  /** Opens the next repeat of one item once its current fill is complete. */
  refill: (body: RefillRequest) =>
    api.post<DispenseViewResponse>('/api/v1/pharmacy/prescriptions/refill', body),
};

/**
 * The dispensing log (`GET /reports/dispenses`). Scoped by the server: a
 * pharmacy reads its own fills, a clinic the fills of its prescriptions, the
 * platform everything. No patient data for any caller.
 */
export const dispenseReportsApi = {
  list: (params?: QueryOf<'/api/v1/reports/dispenses', 'get'> & Record<string, unknown>) =>
    api.get<Paginated<DispenseReportResponse>>('/api/v1/reports/dispenses', { params }),
};

export const notificationsApi = {
  path: '/api/v1/notifications',
  /**
   * `type` is repeatable and ORed (C68, backend `433ccd6`): it goes out as
   * `type=A&type=B` — axios would otherwise write `type[]=A`, which Spring
   * does not bind. An unknown type is a 400, so only the spec's enum is
   * accepted. `total` is the filtered count.
   */
  list: (params?: QueryOf<'/api/v1/notifications', 'get'>) =>
    api.get<Paginated<NotificationResponse>>('/api/v1/notifications', {
      params,
      paramsSerializer: { indexes: null },
    }),
  // The endpoint answers `{ "unread": n }`, not a bare number. Unwrapping here
  // keeps every caller dealing in a count.
  unreadCount: () =>
    api
      .get<ResponseOf<'/api/v1/notifications/unread-count', 'get'>>(
        '/api/v1/notifications/unread-count',
      )
      .then((counts) => counts?.unread ?? 0),
  markRead: (id: string) => api.patch<void>(`/api/v1/notifications/${id}/read`),
  // Answers `{ "updated": n }`; nothing renders it, but the type should not lie.
  markAllRead: () =>
    api.patch<ResponseOf<'/api/v1/notifications/read-all', 'patch'>>(
      '/api/v1/notifications/read-all',
    ),
  remove: (id: string) => api.delete<void>(`/api/v1/notifications/${id}`),
};

export const auditApi = {
  path: '/api/v1/audit-logs',
  list: (params?: Record<string, unknown>) =>
    api.get<Paginated<AuditLogResponse>>('/api/v1/audit-logs', { params }),
};

/**
 * The fleet is the platform's: no tenant scope on any of these. `POST /drivers`
 * creates the driver's user as well — INVITED, with a password nobody holds,
 * until the driver accepts the code sent to their phone.
 */
export const driversApi = {
  ...createResourceApi<DriverResponse, DriverRequest>('/api/v1/drivers'),
  /** Edit never touches the login; `status` other than ACTIVE ends its sessions. */
  update: (id: string, body: DriverUpdateRequest) =>
    api.put<DriverResponse>(`/api/v1/drivers/${id}`, body),
  /**
   * Re-sends the driver's invitation, under DRIVER_MANAGE rather than
   * USER_WRITE, and keyed on the **driver** id: a fleet manager holds neither
   * the permission nor the user id that `/users/{id}/invite` wants. The two
   * endpoints are deliberately separate.
   */
  invite: (id: string) => api.post<DriverResponse>(`/api/v1/drivers/${id}/invite`),
};

export const vehiclesApi = {
  ...createResourceApi<VehicleResponse, VehicleRequest>('/api/v1/vehicles'),
  /**
   * `{ total, withDevice, withCooler, byStatus }` for exactly the rows the
   * list returns under the same filters (`status`, `hasCooler`, `hasDevice`,
   * `search`). Send none for the page header's figures; leave `status` out
   * for tab badges. Every status is present in `byStatus`.
   */
  counts: (params?: QueryOf<'/api/v1/vehicles/counts', 'get'> | Record<string, unknown>) =>
    api.get<ResponseOf<'/api/v1/vehicles/counts', 'get'>>('/api/v1/vehicles/counts', { params }),
};

/**
 * A tracker and the vehicle it is fitted to. There is no device key and no
 * endpoint to replace one any more (backend D4, 2026-10-20): trackers report
 * to Wialon, never to us. `POST`/`PUT` answer 409 `VEHICLE_HAS_TRACKER` when the vehicle
 * already carries another device.
 */
export const gpsDevicesApi = createResourceApi<GpsDeviceResponse, GpsDeviceRequest>(
  '/api/v1/gps-devices',
);

/**
 * Live vehicle tracking (backend phase 1, 2026-10-20). **Platform staff
 * only**, checked twice server-side — the permission and a platform user — so
 * nothing here is ever called from a screen a tenant can open.
 *
 * Every endpoint except `availability` exists **only on a deployment with a
 * Wialon token**. `availability` is unconditional and is the only gate: never
 * infer the feature from a 404.
 *
 * The reads never call Wialon; they read a table the backend's feed keeps
 * current every ~5 s, so polling the overview costs one indexed read.
 */
export const trackingApi = {
  availability: () =>
    api.get<ResponseOf<'/api/v1/tracking/availability', 'get'>>('/api/v1/tracking/availability'),
  /**
   * One complete snapshot: `generatedAt`, the feed's health, every tracked
   * vehicle. `search` (C75, backend `433ccd6`) narrows `vehicles` and
   * `vehiclesWithoutTracker` by plate, make, model, unit name or current
   * driver — case-, space- and Arabic-variant-blind; `feed` and
   * `unlinkedUnitCount` still describe the whole fleet.
   */
  vehicles: (search?: string) =>
    api.get<ResponseOf<'/api/v1/tracking/vehicles', 'get'>>(
      '/api/v1/tracking/vehicles',
      search ? { params: { search } satisfies QueryOf<'/api/v1/tracking/vehicles', 'get'> } : undefined,
    ),
  /** One vehicle, **with** `sensors[]`. No tracker is 200 `NO_TRACKER`, not a 404. */
  vehicle: (vehicleId: string) =>
    api.get<ResponseOf<'/api/v1/tracking/vehicles/{vehicleId}', 'get'>>(
      `/api/v1/tracking/vehicles/${vehicleId}`,
    ),
  /**
   * Where a vehicle was over a window of at most 24 h: route segments, trips,
   * stops, coverage (backend phase 3, `95f0e99`). Read from Wialon on demand
   * and **every 200 is audited** as `TRACKING_HISTORY_VIEWED`, cached or not —
   * so this is called only when a person presses "Show history", never on a
   * timer, a focus or a mount (`useVehicleHistory`).
   *
   * `from`/`to` carry the Asia/Baghdad offset (`+03:00`). They go through
   * axios `params`, whose serializer is `encodeURIComponent`: the `+` leaves
   * as `%2B`, which a raw template string would not do — a bare `+` in a query
   * string is a space, and the backend would refuse the date-time.
   */
  history: (vehicleId: string, window: { from: string; to: string }) =>
    api.get<ResponseOf<'/api/v1/tracking/vehicles/{vehicleId}/history', 'get'>>(
      `/api/v1/tracking/vehicles/${vehicleId}/history`,
      {
        params: {
          from: window.from,
          to: window.to,
        } satisfies QueryOf<'/api/v1/tracking/vehicles/{vehicleId}/history', 'get'>,
      },
    ),
  /** The units the tracking account sees, linked or not. `VEHICLE_MANAGE`. */
  units: () => api.get<ResponseOf<'/api/v1/tracking/units', 'get'>>('/api/v1/tracking/units'),
  /**
   * A deliberate human act, never automatic. 404 `TRACKING_UNIT_NOT_FOUND`,
   * 409 `TRACKING_UNIT_ALREADY_LINKED`, 409 `VEHICLE_HAS_TRACKER`.
   */
  link: (unitId: number, body: LinkUnitRequest) =>
    api.post<ResponseOf<'/api/v1/tracking/units/{unitId}/link', 'post'>>(
      `/api/v1/tracking/units/${unitId}/link`,
      body,
    ),
  /** The device stays fitted; it stops being that unit to us and its live state is dropped. */
  unlink: (unitId: number) =>
    api.delete<ResponseOf<'/api/v1/tracking/units/{unitId}/link', 'delete'>>(
      `/api/v1/tracking/units/${unitId}/link`,
    ),
};

/**
 * Scoped by the backend: a tenant sees what it sent and what is addressed to
 * it, platform staff see everything. Out of scope is 404, never 403. The
 * driver's transitions (picked-up, in-transit, delivered, failed) are the
 * driver app's and are deliberately not here.
 */
export const deliveriesApi = {
  ...createResourceApi<DeliveryResponse, DeliveryRequest>('/api/v1/deliveries'),
  assign: (id: string, body: DeliveryAssignRequest) =>
    api.post<DeliveryResponse>(`/api/v1/deliveries/${id}/assign`, body),
  /*
   * There is no `manage` (`PATCH /deliveries/{id}`): it existed only to move
   * the scheduling window and override the derived temperature range, both of
   * which were removed on 2026-09-27, and the endpoint answers 405. Management
   * assigns, reassigns and cancels; a delivery still REQUESTED is edited
   * through the full PUT.
   */
  cancel: (id: string, body: DeliveryCancelRequest) =>
    api.post<DeliveryResponse>(`/api/v1/deliveries/${id}/cancel`, body),
  /**
   * Per-status figures for exactly the rows the list returns under the same
   * filters (`deliveryCounts`, 2026-10-21). Send the list's query string minus
   * paging, and leave `status` out for tab badges.
   */
  counts: (params?: QueryOf<'/api/v1/deliveries/counts', 'get'> | Record<string, unknown>) =>
    api.get<ResponseOf<'/api/v1/deliveries/counts', 'get'>>('/api/v1/deliveries/counts', { params }),
};

/**
 * Reading a supplier invoice instead of typing a delivery's items
 * (2026-10-19). `DELIVERY_REQUEST` or `DELIVERY_MANAGE` — whoever may create
 * the delivery the invoice is for.
 *
 * The file is **not** posted here. It goes through `POST /api/v1/files` like
 * every other upload, and the `objectKey` is sent instead: one upload, the
 * invoice is kept, and the same key is what the inserted lines carry as their
 * attachment.
 */
export const invoiceExtractionsApi = {
  /**
   * Whether this deployment has the service and a bucket to keep the file in,
   * and the limits to quote at the user. **Unconditional**: it exists on every
   * deployment and answers `available: false` when the rest does not, which is
   * why the upload control is drawn from this and never from a 404 on `POST`.
   */
  availability: () =>
    api.get<ResponseOf<'/api/v1/invoice-extractions/availability', 'get'>>(
      '/api/v1/invoice-extractions/availability',
    ),
  /**
   * **202** with the `QUEUED` job, already committed, so the id can be polled
   * at once. Refusals are `429 INVOICE_EXTRACTION_QUEUE_FULL` and
   * `429 INVOICE_EXTRACTION_RATE_LIMITED`, both with `Retry-After` — neither
   * is a failure, and neither deserves a red toast.
   */
  queue: (body: InvoiceExtractionRequest) =>
    api.post<ResponseOf<'/api/v1/invoice-extractions', 'post'>>(
      '/api/v1/invoice-extractions',
      body,
    ),
  get: (id: string) =>
    api.get<InvoiceExtractionResponse>(`/api/v1/invoice-extractions/${id}`),
  /**
   * Newest first, optional `status`. This is how somebody who closed the tab
   * gets back to a finished extraction: they hold no id to poll.
   */
  list: (params?: QueryOf<'/api/v1/invoice-extractions', 'get'>) =>
    api.get<Paginated<InvoiceExtractionResponse>>('/api/v1/invoice-extractions', { params }),
  /**
   * Delete the uploaded file and the extracted result. **200** with the job as
   * it now stands: `purgedAt` set, `objectKey` / `invoice` / `meta` null.
   *
   * The row itself is kept on purpose — a billed call to an external service
   * stays reconcilable — so this is a purge, not a delete, and repeating it is
   * a no-op answered 200. Three refusals, none of them generic:
   * `409 INVOICE_EXTRACTION_IN_PROGRESS` (still queued or running, settles on
   * its own), `409 INVOICE_EXTRACTION_IN_USE` with the order codes in
   * `details.deliveries`, and `500 FILE_DELETE_FAILED` with nothing destroyed.
   */
  remove: (id: string) =>
    api.delete<ResponseOf<'/api/v1/invoice-extractions/{id}', 'delete'>>(
      `/api/v1/invoice-extractions/${id}`,
    ),
};

/**
 * The Ministry of Health's **Gudea** medicines register, mirrored by the
 * backend (2026-10-04). Authenticated, no permission: a pharmacy matching a
 * pack reads it as well as a prescriber, which is why it is not behind
 * `PRESCRIPTION_WRITE` like the learned suggestions are.
 *
 * `search` answers a **bare array** capped at 50, not a page — autocomplete
 * does not page, and the length is how a caller knows it was truncated.
 */
export const medicinesApi = {
  search: (params: MedicineSearchQuery) =>
    api.get<ResponseOf<'/api/v1/medicines', 'get'>>('/api/v1/medicines', { params }),
  importRuns: (params?: { page?: number; size?: number }) =>
    api.get<Paginated<ImportRunResponse>>('/api/v1/medicines/import-runs', { params }),
  /**
   * Starts the crawl in the background and answers **202** with the `RUNNING`
   * run, already committed and immediately pollable from `importRuns`
   * (C43, 2026-10-05). The crawl itself is a few thousand throttled pages and
   * over an hour, and does not depend on this request. A second call while one
   * runs is `409 REGISTRY_IMPORT_ALREADY_RUNNING`, which the screen reads as
   * "a crawl is already in flight", not as a failure.
   *
   * `resume` stays on the signature because the endpoint has it, but
   * `RegisterImportsPage` always sends the default — see the reasoning there.
   */
  startImport: (resume = true) =>
    api.post<ImportRunResponse>('/api/v1/medicines/import', undefined, { params: { resume } }),
};

/**
 * The Iraqi Pharmacists Syndicate's three registers, mirrored by the backend
 * (2026-10-04) for the registration form's name autocomplete. `search` is
 * **allowlisted for an unverified organization** — an applicant still in
 * `DRAFT` is exactly who calls it — and answers a bare array.
 */
export const syndicateRegistryApi = {
  search: (params: SyndicateRegistrySearchQuery) =>
    api.get<ResponseOf<'/api/v1/syndicate-registry', 'get'>>('/api/v1/syndicate-registry', {
      params,
    }),
  importRuns: (params?: { source?: string; page?: number; size?: number }) =>
    api.get<Paginated<ImportRunResponse>>('/api/v1/syndicate-registry/import-runs', { params }),
  /**
   * Runs the weekly import now: synchronous, a few seconds, and **always 200**
   * — each register's outcome is its own run record, not an HTTP status, so
   * the caller refetches the runs rather than reading the response.
   */
  startImport: () =>
    api.post<ImportRunResponse[]>('/api/v1/syndicate-registry/import'),
};

export const governoratesApi = {
  list: () =>
    api.get<ResponseOf<'/api/v1/public/governorates', 'get'>>('/api/v1/public/governorates'),
  get: (id: string) => api.get<GovernorateResponse>(`/api/v1/public/governorates/${id}`),
};
